import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Calendar,
  DollarSign,
  Home,
  Package,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

import BaseUrl from "../../../constants/BaseUrl";
import ParagraphTag from "../../reusebale/Paragraph";
import ImageTag from "../../reusebale/imageTag";

const PlanDetails = () => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loginSuccess?.user?._id || !loginSuccess?.token) return;

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess.token}`);

    fetch(
      `${BaseUrl}landlord/profile/getAllSubscriptions/${loginSuccess.user._id}`,
      { method: "GET", headers: myHeaders, redirect: "follow" }
    )
      .then((res) => res.json())
      .then((result) => {
        if (result.status === "Success" && result.data) {
          // Only show active and pending (non-invalid, non-inactive) subscriptions
          const relevant = result.data.filter(
            (s) => s.status === "active" || s._computedStatus === "activation_pending"
          );
          setSubscriptions(relevant);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [loginSuccess?.user?._id, loginSuccess?.token]);

  const totalSpent = subscriptions.reduce((sum, s) => sum + (s.amount || 0), 0);
  const totalProperties = subscriptions.reduce(
    (sum, s) => sum + (s.packageData?.allowProperties || 0),
    0
  );

  const mainPlan = subscriptions.find(
    (s) => !s.subscriptionPlanName?.includes("Add-on")
  );

  const isAddOn = (sub) => sub.subscriptionPlanName?.includes("Add-on");

  const getSubscriptionTypeBadge = (sub) => {
    if (isAddOn(sub)) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <TrendingUp size={12} className="mr-1" />
          Property Add-on
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle size={12} className="mr-1" />
        Initial Purchase
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="h-full pt-5 w-full">
        <div className="bg-white w-full h-[87%] rounded-lg px-4 py-4 flex items-center justify-center">
          <ParagraphTag
            content="Loading..."
            classes="text-darkColor/40 font-popinsMedium text-sm"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full pt-5 w-full">
      <div className="bg-white w-full h-[87%] rounded-lg px-4 py-4">
        <div className="h-full">
          {/* Header */}
          <div className="flex flex-col mb-4">
            <ParagraphTag
              content="Plan Details"
              classes="text-darkColor font-popinsSemiBold text-lg"
            />
            <div className="flex items-center space-x-1 mt-1">
              <Package className="text-themeColor" size={16} />
              <ParagraphTag
                content={`${subscriptions.length} Transaction${
                  subscriptions.length !== 1 ? "s" : ""
                }`}
                classes="text-darkColor/60 font-popinsMedium text-xs"
              />
            </div>
          </div>

          {subscriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <ImageTag
                path="/assets/daashboard/nodata.png"
                classes="w-[60px] mb-3"
                altText="No subscriptions"
              />
              <ParagraphTag
                content="No Active Subscriptions"
                classes="text-darkColor font-popinsSemiBold text-sm mb-1"
              />
              <ParagraphTag
                content="You don't have any active subscription plans yet."
                classes="text-darkColor/60 font-popinsMedium text-xs text-center"
              />
            </div>
          ) : (
            <div className="h-[85%] overflow-y-auto">
              {/* Quick Summary Cards - Stacked */}
              <div className="space-y-3 mb-4">
                {/* Total Spent Card */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="bg-green-500 p-1.5 rounded-md">
                        <DollarSign className="text-white" size={16} />
                      </div>
                      <div>
                        <ParagraphTag
                          content="Total Spent"
                          classes="text-green-700 font-popinsMedium text-xs"
                        />
                        <ParagraphTag
                          content={formatCurrency(totalSpent)}
                          classes="text-green-800 font-popinsBold text-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Properties Card */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="bg-blue-500 p-1.5 rounded-md">
                        <Home className="text-white" size={16} />
                      </div>
                      <div>
                        <ParagraphTag
                          content="Properties Allowed"
                          classes="text-blue-700 font-popinsMedium text-xs"
                        />
                        <ParagraphTag
                          content={`${totalProperties} Properties`}
                          classes="text-blue-800 font-popinsBold text-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Plan Card */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="bg-purple-500 p-1.5 rounded-md">
                        <Package className="text-white" size={16} />
                      </div>
                      <div>
                        <ParagraphTag
                          content="Main Plan"
                          classes="text-purple-700 font-popinsMedium text-xs"
                        />
                        <ParagraphTag
                          content={mainPlan?.packageData?.planName || "-"}
                          classes="text-purple-800 font-popinsBold text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Subscription Cards - Compact Version */}
              <div className="space-y-3">
                <ParagraphTag
                  content="Subscription Details"
                  classes="text-darkColor font-popinsSemiBold text-sm mb-2"
                />

                {subscriptions.map((subscription) => (
                  <div
                    key={subscription._id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <ParagraphTag
                          content={subscription.packageData?.planName || subscription.subscriptionPlanName}
                          classes="text-darkColor font-popinsSemiBold text-sm"
                        />
                        <div className="mt-1">
                          {getSubscriptionTypeBadge(subscription)}
                        </div>
                      </div>
                      <div className="text-right">
                        <ParagraphTag
                          content={formatCurrency(subscription.amount)}
                          classes="text-themeColor font-popinsBold text-lg"
                        />
                      </div>
                    </div>

                    {/* Key Metrics - 2x2 Grid */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {/* Properties */}
                      <div className="bg-white p-2 rounded border">
                        <div className="flex items-center space-x-1">
                          <Home className="text-blue-600" size={14} />
                          <div>
                            <ParagraphTag
                              content={subscription.packageData?.allowProperties?.toString() || "-"}
                              classes="text-darkColor font-popinsSemiBold text-sm"
                            />
                            <ParagraphTag
                              content="Properties"
                              classes="text-darkColor/60 font-popinsMedium text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Monthly Price */}
                      <div className="bg-white p-2 rounded border">
                        <div className="flex items-center space-x-1">
                          <DollarSign className="text-green-600" size={14} />
                          <div>
                            <ParagraphTag
                              content={formatCurrency(
                                subscription.packageData?.monthlyPrice
                              )}
                              classes="text-darkColor font-popinsSemiBold text-sm"
                            />
                            <ParagraphTag
                              content="Monthly"
                              classes="text-darkColor/60 font-popinsMedium text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Price Per Property */}
                      <div className="bg-white p-2 rounded border">
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="text-purple-600" size={14} />
                          <div>
                            <ParagraphTag
                              content={formatCurrency(
                                subscription.packageData?.pricePerProperty
                              )}
                              classes="text-darkColor font-popinsSemiBold text-sm"
                            />
                            <ParagraphTag
                              content="Per Property"
                              classes="text-darkColor/60 font-popinsMedium text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="bg-white p-2 rounded border">
                        <div className="flex items-center space-x-1">
                          <Clock className="text-orange-600" size={14} />
                          <div>
                            <ParagraphTag
                              content={`${subscription.packageData?.numberOfMonth || "-"}m`}
                              classes="text-darkColor font-popinsSemiBold text-sm"
                            />
                            <ParagraphTag
                              content="Duration"
                              classes="text-darkColor/60 font-popinsMedium text-xs"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Subscription Period - Compact */}
                    <div className="bg-white p-2 rounded border">
                      <div className="flex items-center space-x-2">
                        <Calendar className="text-themeColor" size={14} />
                        <div className="flex-1">
                          <ParagraphTag
                            content="Period"
                            classes="text-darkColor font-popinsMedium text-xs"
                          />
                          <ParagraphTag
                            content={`${moment(subscription.startDate).format(
                              "MM/DD/YYYY"
                            )} - ${moment(subscription.endDate).format(
                              "MM/DD/YYYY"
                            )}`}
                            classes="text-darkColor/60 font-popinsMedium text-xs"
                          />
                        </div>

                        {/* Discount Info - If Available */}
                        {subscription.packageData?.discountAmount &&
                          subscription.packageData.discountAmount !== "$0" && (
                            <div className="text-right">
                              <ParagraphTag
                                content={
                                  subscription.packageData.discountAmount
                                }
                                classes="text-green-600 font-popinsSemiBold text-xs"
                              />
                              <ParagraphTag
                                content="Saved"
                                classes="text-green-600 font-popinsMedium text-xs"
                              />
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Invoice Info */}
                    <div className="mt-2 text-center">
                      <ParagraphTag
                        content={`INV-${subscription._id?.slice(-8)}`}
                        classes="text-darkColor/60 font-popinsMedium text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanDetails;
